/**
 * /onboard/setup/import/:runId/wizard
 *
 * Loads inferences + technician profiles, converts them to the
 * LeanSetupWizard's `initial` shape via `inferenceLoader`, then
 * renders the wizard with `prefillSource='import'`.
 *
 * The wizard itself is the only live shop-config commit point —
 * this page just hands it pre-baked starting values.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { LeanSetupWizard } from '@/components/onboarding/LeanSetupWizard';
import {
  buildWizardInitialWithRoster,
  inferredSetupFields,
  type SetupInferenceRow,
  type TechnicianInferredProfileRow,
} from '@/lib/onboarding/inferenceLoader';
import { useImportRun } from '@/hooks/onboarding/useImportRun';

const INFERENCE_TABLE = 'shop_setup_inferences' as unknown as Parameters<typeof supabase.from>[0];
const TECH_PROFILE_TABLE =
  'technician_inferred_profile' as unknown as Parameters<typeof supabase.from>[0];
// Batch 9B: read distinct advisor display names directly from the
// permanent imported-history table. The CSV parser captures
// `advisorDisplayName` per RO, but the inference layer in
// supabase/functions/_shared/shopHistoryImport/inference/technician.ts
// intentionally discards advisors (line: `// Discard advisor
// fallback`). Until a dedicated advisor inference exists, this
// lightweight read is the smallest safe source.
const SHOP_HISTORY_RO_TABLE =
  'shop_history_repair_orders' as unknown as Parameters<typeof supabase.from>[0];

/**
 * Batch 9B: aggregate distinct, non-empty advisor names from the
 * shop's imported history. Returns names ordered by frequency
 * descending — the wizard uses this same order to seed Step 3
 * SERVICE_WRITER rows. Empty/whitespace names are filtered.
 *
 * RLS: `Shop members can view imported repair orders` policy (see
 * migration 20260601180000) gates this read on shop membership; an
 * operator going through onboarding always satisfies it.
 */
async function fetchDistinctAdvisorNames(shopId: string): Promise<string[]> {
  // deno-lint-ignore no-explicit-any
  const client = supabase as any;
  const { data, error } = await client
    .from(SHOP_HISTORY_RO_TABLE)
    .select('advisor_display_name')
    .eq('shop_id', shopId)
    .not('advisor_display_name', 'is', null);
  if (error) throw error;
  const counts = new Map<string, number>();
  for (const row of (data ?? []) as Array<{ advisor_display_name: string | null }>) {
    const name = (row.advisor_display_name ?? '').trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
}

async function fetchInferenceBundle(shopId: string) {
  // Untyped table cast — see useImportRun for the same pattern + reason.
  // deno-lint-ignore no-explicit-any
  const client = supabase as any;
  const [inf, prof, advisorNames] = await Promise.all([
    client
      .from(INFERENCE_TABLE)
      .select('inference_kind, confidence, payload')
      .eq('shop_id', shopId),
    client
      .from(TECH_PROFILE_TABLE)
      .select(
        'external_technician_id, display_name, job_count, total_labor_hours, median_labor_hours, comeback_rate, warranty_rate, appears_full_time, top_jobs',
      )
      .eq('shop_id', shopId)
      .eq('is_stale', false),
    fetchDistinctAdvisorNames(shopId),
  ]);
  if (inf.error) throw inf.error;
  if (prof.error) throw prof.error;
  return {
    inferences: ((inf.data ?? []) as unknown) as SetupInferenceRow[],
    technicianProfiles: ((prof.data ?? []) as unknown) as TechnicianInferredProfileRow[],
    advisorNames,
  };
}

const OnboardSetupImportWizard = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { activeShop, user } = useAuth();
  const importEnabled = useFeatureFlag('importEnabledForInternal');

  // Load the run so we have the shop's snapshotted time_zone.
  const { run } = useImportRun({
    shopId: activeShop?.id ?? null,
    importRunId: runId ?? null,
    enabled: Boolean(importEnabled && activeShop?.id && runId),
  });

  const inferenceQuery = useQuery({
    queryKey: ['shop_setup_inferences_bundle', activeShop?.id],
    enabled: Boolean(importEnabled && activeShop?.id),
    queryFn: () => fetchInferenceBundle(activeShop!.id),
  });

  useEffect(() => {
    if (importEnabled === false) {
      navigate('/onboard/setup', { replace: true });
    }
  }, [importEnabled, navigate]);

  if (!user || !activeShop) return null;
  if (!runId) {
    return (
      <div className="mx-auto max-w-2xl p-4" data-testid="wizard-missing-run">
        <Card>
          <CardHeader>
            <CardTitle>Import not found</CardTitle>
            <CardDescription>Missing run id.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (inferenceQuery.isLoading || !inferenceQuery.data) {
    return (
      <div className="mx-auto max-w-2xl p-4" data-testid="import-wizard-loading">
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            Preparing your prefilled setup…
          </CardContent>
        </Card>
      </div>
    );
  }
  if (inferenceQuery.isError) {
    return (
      <div className="mx-auto max-w-2xl p-4" data-testid="import-wizard-error">
        <Card>
          <CardContent className="py-4 text-sm text-destructive">
            Could not load inferences. Refresh to retry.
          </CardContent>
        </Card>
      </div>
    );
  }

  const { initial, suggestedTechnicianRoster, suggestedAdvisorRoster } =
    buildWizardInitialWithRoster({
      inferences: inferenceQuery.data.inferences,
      technicianProfiles: inferenceQuery.data.technicianProfiles,
      advisorNames: inferenceQuery.data.advisorNames,
      shopName: activeShop.name ?? '',
      timeZone:
        run?.time_zone ??
        (activeShop as { time_zone?: string }).time_zone ??
        'America/Los_Angeles',
    });

  return (
    <div data-testid="onboard-import-wizard-page">
      <LeanSetupWizard
        prefillSource="import"
        importRunId={runId}
        inferredFields={inferredSetupFields(inferenceQuery.data.inferences)}
        initial={initial}
        suggestedTechnicianRoster={suggestedTechnicianRoster}
        suggestedAdvisorRoster={suggestedAdvisorRoster}
      />
    </div>
  );
};

export default OnboardSetupImportWizard;
